export interface Supervisor {
  name: {
    first: string;
    middle: string;
    last: string;
  };
  employeeID: number;
  altEmployeeID: string;
  count: number;
}

export interface EmployeeTimeSheet {
  altEmployeeID: string;
  employeeID: string;
  name: string;
}

// Payroll-approved API response: nested types for days / projectEmployeeTasks / miscEmployeeTasks
export interface PayrollApprovedMiscEmployeeTask {
  miscEmployeeTaskID: number;
  projectID?: number;
  accountCode?: { description?: string; name?: string; altAccountCodeID?: string; accountCodeID: number };
  amountClaimed?: number;
  miscDescription?: string;
  superDescription?: string | null;
  /**
   * TAS-360 — `MiscEmployeeTask.ApprovalStatus`: 1 FirstPending · 2 SecondPending ·
   * 3 Approved · 4 Denied (see `MISC_TASK_STATUS`).
   *
   * OPTIONAL ON PURPOSE, and `undefined` is a real third state rather than a
   * missing 1. The column ships with the NPM two-level-approval DDL and the API
   * only selects it when `MISC_TASK_TWO_LEVEL_APPROVAL` is on, so an API that has
   * not been upgraded — or any other tenant — sends the field absent.
   *
   * NOT USED BY THE VISTA EXPORT GATE, deliberately. That gate keys on a blank
   * `superDescription` alone — see `isMiscTaskPendingApproval` in the two Vista
   * batch export modals and `isBlocked` in `Pages/v2/vistaExport/vistaExportHelpers`
   * for why keying on this field instead over-blocked NPM.
   */
  approvalStatus?: number;
  intermediateName?: string;
  altIntermediateID?: string;
  insertDateTime?: string;
  insertDateTimeOffset?: string;
}

export interface PayrollApprovedProjectEmployeeTasks {
  project?: { name?: string; altProjectID?: string; projectID?: number };
  employeeTasks?: unknown[];
  miscEmployeeTasks?: PayrollApprovedMiscEmployeeTask[];
  totalAmountClaimed?: number;
}

/** A punch. `type` is TimeEntryType — 1 Regular, 2 Meal; only Regular ever exports. */
export interface PayrollApprovedPunch {
  type?: number;
  inDateTime?: string | null;
  outDateTime?: string | null;
}

/**
 * Hourly work, grouped by project/phase/account/service-order.
 *
 * THIS IS WHERE AN HOURLY NPM EMPLOYEE'S WHOLE WEEK LIVES — `day.timeEntries` is the
 * *piece* punch table and is routinely empty for them. Counting only `timeEntries`
 * reports "0 time" for every hourly tech on the crew.
 *
 * The nested `project`/`account`/`projectPhase` objects are always constructed by the
 * API mapper even when the join missed, so test their members, never the object.
 */
export interface PayrollApprovedHourlyProject {
  project?: { altProjectID?: string | null; altProjectID2?: string | null };
  account?: { accountCodeID?: number | null };
  projectPhase?: { altProjectPhaseID?: string | null };
  serviceOrderText?: string | null;
  /**
   * TAS-515 — the NPM service order + scope flattened onto the group by the API
   * (`HourlyProjectGroup`, src/db/timesheetsUtil.ts). Present only on NPM; every other
   * tenant sends them absent, so nothing here can change another tenant's export.
   *
   * TAS-517: `serviceOrderID` and `tempServiceOrderDescription` ARE part of the
   * export-volume test now. The exporter's service-order branch takes all three shapes
   * (see `src/util/serviceOrderRow.ts`), so `volumeOf` counts all three too.
   */
  serviceOrderAltID?: string | null;
  serviceOrderID?: number | null;
  serviceOrderScopeID?: number | null;
  scopeSeq?: number | null;
  scopeDescription?: string | null;
  scopeDivision?: string | null;
  scopeJob?: string | null;
  tempServiceOrderDescription?: string | null;
  timeEntriesHourly?: PayrollApprovedPunch[];
}

export interface PayrollApprovedDay {
  date?: string;
  regularTime?: number;
  overTime?: number;
  doubleTime?: number;
  totalWorkMinutes?: number;
  totalClaimedAmount?: number;
  /** Raw piece punches. Empty for an hourly employee — see PayrollApprovedHourlyProject. */
  timeEntries?: PayrollApprovedPunch[];
  hourlyProjects?: PayrollApprovedHourlyProject[];
  projectEmployeeTasks?: PayrollApprovedProjectEmployeeTasks[];
  employeeSickClaims?: unknown[];
  employeeOperationalTasks?: unknown[];
  employeeCustomerServiceWarrantyTasks?: unknown[];
  fiveHourLunchException?: boolean;
  fullLunchException?: boolean;
  noLunchException?: boolean;
  hasModifiedDuty?: boolean;
  modifiedDutyRate?: number;
}

/** Full payroll-approved timesheet response (one item in the array from /admin/timesheets/payrollApproved) */
export interface EmployeeTimeSheetWithDays extends Omit<EmployeeTimeSheet, 'name' | 'employeeID'> {
  employeeID: number | string;
  name: string | { first: string; middle?: string; last: string };
  /** `kioskUser` decides how raw punches export: one entry each, or one per day. */
  role?: { kioskUser?: boolean };
  days?: PayrollApprovedDay[];
}

export enum JobStatusEnum {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING", 
  COMPLETED = "COMPLETED",
  FAILED = "FAILED"
}

export type PiecePostStatus = "pending" | "posting" | "succeeded" | "failed" | "skipped";

export interface RegularPiecePostVistaBody {
  OrderNo?: number;
  AmountClaimed?: string | number;
  Employee?: number;
  JCCo?: number;
  PREndDate?: string;
}

export interface RegularPiecePost {
  indexInEmployee: number;
  status: PiecePostStatus;
  errorMessage?: string | null;
  errorDetails?: unknown;
  vistaBody?: RegularPiecePostVistaBody;
}

/**
 * One miscellaneous piece ("red") pipeline: create in udJCPieceworks, then submit to
 * udJCSubmittedPiecew.
 *
 * `failedStep` names which half broke, and the two are NOT symmetric:
 *   - "udJCPieceworks"     the CREATE failed. `vistaOrderNoAfterCreate` is forced null —
 *                          nothing was left behind in Vista.
 *   - "udJCSubmittedPiecew" the create SUCCEEDED and the submit failed. This is the orphan
 *                          case: Vista is holding an order with nothing claimed against it,
 *                          and `vistaOrderNoAfterCreate` carries its number (though it can
 *                          still be null if Vista returned no OrderNo).
 * Not reachable in production today — POST_MISC_PIECEWORKS_TO_VISTA is false in the API, so
 * every misc piece comes back `skipped`.
 */
export interface MiscPiecePost {
  indexInEmployee: number;
  status: PiecePostStatus;
  errorMessage?: string | null;
  errorDetails?: unknown;
  failedStep?: "udJCPieceworks" | "udJCSubmittedPiecew" | null;
  vistaOrderNoAfterCreate?: number | null;
  snapshot?: { jcPieceworks?: Record<string, unknown>; submittedTemplate?: RegularPiecePostVistaBody };
}

/** V2 progress: per-employee time/pieces/misc status (optional on JobStatus for backward compatibility) */
export interface JobProgressEmployee {
  altEmployeeID?: string;
  firstName?: string;
  lastName?: string;
  timeBatchTotal?: number;
  timeBatchInProgress?: number;
  timeBatchCompleted?: number;
  timeBatchRemaining?: number;
  timeBatchError?: string;
  piecesTotal?: number;
  piecesInProgress?: number;
  piecesCompleted?: number;
  piecesRemaining?: number;
  piecesError?: string;
  /** Per regular (non-misc) piece Vista post progress; preferred over piecesError for UI */
  regularPiecePosts?: RegularPiecePost[];
  miscPiecesTotal?: number;
  miscPiecesInProgress?: number;
  miscPiecesCompleted?: number;
  miscPiecesRemaining?: number;
  miscPiecesError?: string;
  /** Per misc ("red") piece Vista post progress; preferred over miscPiecesError for UI */
  miscPiecePosts?: MiscPiecePost[];
}

/** V2 progress data returned by jobStatus for V2 export jobs */
export interface JobProgressData {
  timecardBatchCreation?: string;
  timecardBatchCreationError?: string;
  totalTimeBatchEntries?: number;
  totalPieces?: number;
  totalMiscPieces?: number;
  inProgressTimeBatch?: number;
  inProgressPieces?: number;
  inProgressMiscPieces?: number;
  completedTimeBatch?: number;
  completedPieces?: number;
  completedMiscPieces?: number;
  remainingTimeBatch?: number;
  remainingPieces?: number;
  remainingMiscPieces?: number;
  employees?: JobProgressEmployee[];
}

export interface JobStatus {
  jobStatusID: number;
  jobType: string;
  status: JobStatusEnum;
  progress: number;
  currentStep: string;
  createdDateTime: string;
  updatedDateTime: string;
  completedDateTime: string | null;
  inputData: string;
  resultData: any;
  /** V2: optional detailed progress (time/pieces/misc per employee) */
  progressData?: JobProgressData;
}

export interface JobStartResponse {
  jobID: number;
  status: JobStatusEnum;
}